import DashboardSummaryCountsWidget from '../../../../components/DashboardSummaryCountsWidget/SummaryCountWidget'
import SvgIconProjects from '../../../../assets/inlineSvg/icon-projects.svg'
import SvgIconPolicies from '../../../../assets/inlineSvg/icon-policies.svg'
import SvgIconTickets from '../../../../assets/inlineSvg/icon-tickets.svg'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_TICKETS
} from '../../../../constants'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import { RootState } from '../../../../rootReducer'
import {
  fetchJiraProjectsCount,
  fetchJiraPIITicketsCount,
  FetchJiraPIITicketsCountParams
} from '../../../summary/summarySlice'
import useUrlContext from '../../../../hooks/useUrlContext'
import { mockJiraSummaryCountsWidgetData } from '../../renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../../mainDashboardv2/constants'
import { getDataSourceBasePath } from '../../../../utils/dataSourceUtil'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  projectsCount?: number
  ticketsWithPIICount?: number
  policiesCount?: number
}

interface DispatchProps {
  fetchJiraProjectsCount: (args: { datasourceId: string; [UNIQUE_WIDGET_INDEX]?: string }) => void
  fetchJiraPIITicketsCount: (params: FetchJiraPIITicketsCountParams) => void
}

type JiraSummaryCountsWidgetComponentProps = OwnProps & StateProps & DispatchProps

const JiraSummaryCountsWidget = ({
  fetchJiraProjectsCount,
  fetchJiraPIITicketsCount,
  projectsCount,
  ticketsWithPIICount,
  policiesCount,
  useMockData = false,
  widgetDatasourceId,
  widgetDashboardId,
  widgetDatasourceType
}: JiraSummaryCountsWidgetComponentProps) => {
  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId
    ? widgetDatasourceId
    : context.contextFilters.datasourceId || ''
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  useEffect(() => {
    if (useMockData) return
    if (datasourceId) {
      fetchJiraProjectsCount({ datasourceId, [UNIQUE_WIDGET_INDEX]: widgetIndex })
      fetchJiraPIITicketsCount({ datasourceId, [UNIQUE_WIDGET_INDEX]: widgetIndex })
    }
  }, [fetchJiraProjectsCount, fetchJiraPIITicketsCount, datasourceId, useMockData, widgetIndex])

  const pathArr = getDataSourceBasePath(
    widgetDatasourceType,
    datasourceId,
    context.pathname,
    true
  ) as string[]
  const baseUrl = pathArr.join('/')
  const widgetData = [
    {
      icon: <SvgIconTickets />,
      text: intl.formatMessage({ id: 'dashboard.jira.widget.text.tickets' }),
      count: ticketsWithPIICount || 0,
      link: getUpdatedUrl({
        pathName: baseUrl + URL_TICKETS,
        pageName: URL_TICKETS,
        pageQueryParams: { [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list },
        queryParams: {
          ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
          ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
        }
      })
    },
    {
      icon: <SvgIconProjects />,
      text: intl.formatMessage({ id: 'dashboard.generic.widget.text.projects' }),
      count: projectsCount || 0
    },
    {
      icon: <SvgIconPolicies />,
      text: intl.formatMessage({ id: 'dashboard.dataSource.widget.text.policies' }),
      count: policiesCount || 0
    }
  ]

  const isPoliciesCountAvailable = policiesCount !== undefined
  const isJiraSummaryAvailable = ticketsWithPIICount !== undefined && projectsCount !== undefined
  const countsAvailable = isJiraSummaryAvailable && isPoliciesCountAvailable
  const showLoader = !countsAvailable

  return (
    <DashboardSummaryCountsWidget
      summaryItems={widgetData}
      showLoader={showLoader}
      useMockData={useMockData}
    />
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
  return {
    policiesCount: ownProps?.useMockData
      ? mockJiraSummaryCountsWidgetData.policiesCount
      : state.dashboardSummaryCounts.policiesCount?.[widgetIndex],
    projectsCount: ownProps?.useMockData
      ? mockJiraSummaryCountsWidgetData.projectsCount
      : state.summary.widgetJiraSummaryData?.[widgetIndex]?.projectsCount,
    ticketsWithPIICount: ownProps?.useMockData
      ? mockJiraSummaryCountsWidgetData.ticketsWithPIICount
      : state.summary.widgetJiraSummaryData?.[widgetIndex]?.ticketsWithPIICount
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchJiraPIITicketsCount,
  fetchJiraProjectsCount
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(JiraSummaryCountsWidget)
